import {
  DRAFT_LOCAL_SCHEMA_VERSION,
  DEFAULT_DRAFT_SYNC_STATE,
  getSerializedEditorState,
  loadReportDraft,
  normalizeDraftSyncState,
  normalizeStoredDraftPayload,
  saveReportDraft,
} from './reportDraftStorage';
import {
  ReportDraftApiError,
  getReportDraftFromServer,
  putReportDraftToServer,
} from '../services/reportDraftSyncApi';
import { isReportDraftLocalCacheEnabled } from './reportDraftPersistenceConfig';

const createRandomId = () => {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `draft-client-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const sanitizeTimestamp = (value, fallback = null) => (
  typeof value === 'string' && value.trim() && !Number.isNaN(new Date(value).getTime())
    ? value
    : fallback
);

const toNumberOrDefault = (value, fallback = 0) => (
  Number.isFinite(Number(value)) ? Number(value) : fallback
);

export const createDraftClientInstanceId = () => createRandomId();

export const normalizeDraftPayload = (draft, defaults = {}) => {
  const normalizedDraft = normalizeStoredDraftPayload(draft);
  const base = normalizedDraft || {};
  const reportId = defaults.reportId || base.reportId || null;
  const locale = defaults.locale || base.locale || null;
  const editorState = getSerializedEditorState(base) || getSerializedEditorState(defaults) || null;
  if (!editorState) {
    return null;
  }

  const defaultSync = normalizeDraftSyncState(DEFAULT_DRAFT_SYNC_STATE);
  const nextSync = normalizeDraftSyncState({
    ...defaultSync,
    ...(base.sync || {}),
    ...(defaults.sync || {}),
  });

  return {
    ...base,
    schemaVersion: DRAFT_LOCAL_SCHEMA_VERSION,
    reportId,
    locale,
    templateVersion:
      typeof (defaults.templateVersion ?? base.templateVersion) === 'string'
        ? (defaults.templateVersion ?? base.templateVersion)
        : null,
    timestamp: sanitizeTimestamp(
      defaults.timestamp ?? base.timestamp,
      new Date().toISOString()
    ),
    editorState,
    clientInstanceId:
      (typeof defaults.clientInstanceId === 'string' && defaults.clientInstanceId.trim())
      || (typeof base.clientInstanceId === 'string' && base.clientInstanceId.trim())
      || createDraftClientInstanceId(),
    clientRevision: toNumberOrDefault(defaults.clientRevision ?? base.clientRevision, 0),
    sync: nextSync,
  };
};

export const mapServerDraftToLocalPayload = (serverDraft, options = {}) => {
  if (!serverDraft || typeof serverDraft !== 'object' || typeof serverDraft.editorState !== 'string') {
    return null;
  }
  const localDraft = normalizeDraftPayload(options.localDraft || null, {
    reportId: serverDraft.reportId || options.reportId || null,
    locale: serverDraft.locale || options.locale || null,
  });
  return normalizeDraftPayload(
    {
      ...(localDraft || {}),
      reportId: serverDraft.reportId || options.reportId || localDraft?.reportId || null,
      locale: serverDraft.locale || options.locale || localDraft?.locale || null,
      templateVersion: serverDraft.templateVersion || localDraft?.templateVersion || null,
      editorState: serverDraft.editorState,
      timestamp: sanitizeTimestamp(serverDraft.updatedAt, new Date().toISOString()),
      sync: {
        ...(localDraft?.sync || {}),
        status: 'synced',
        serverRevision: toNumberOrDefault(serverDraft.serverRevision, 1),
        lastSyncedAt: sanitizeTimestamp(serverDraft.updatedAt, new Date().toISOString()),
        lastErrorCode: null,
        lastErrorMessage: null,
        retryAt: null,
      },
    },
    {
      reportId: serverDraft.reportId || options.reportId || localDraft?.reportId || null,
      locale: serverDraft.locale || options.locale || localDraft?.locale || null,
      clientInstanceId: localDraft?.clientInstanceId || createDraftClientInstanceId(),
      clientRevision: toNumberOrDefault(localDraft?.clientRevision, 0),
    }
  );
};

const chooseBestDraft = (localDraft, serverBackedLocalDraft) => {
  if (!localDraft) {
    return serverBackedLocalDraft || null;
  }
  if (!serverBackedLocalDraft) {
    return localDraft;
  }

  const localSyncStatus = localDraft.sync?.status || 'local_only';
  const localClientRevision = toNumberOrDefault(localDraft.clientRevision, 0);
  const localServerRevision = toNumberOrDefault(localDraft.sync?.serverRevision, 0);
  const serverRevision = toNumberOrDefault(serverBackedLocalDraft.sync?.serverRevision, 0);

  if (localSyncStatus !== 'synced' && localClientRevision > 0) {
    return localDraft;
  }
  if (serverRevision > localServerRevision) {
    return serverBackedLocalDraft;
  }
  return localDraft;
};

export const saveLocalDraftSnapshot = async ({ storageKey, payload }) => {
  const normalized = normalizeDraftPayload(payload);
  if (!normalized) {
    throw new Error('Cannot save draft: invalid payload');
  }
  if (!isReportDraftLocalCacheEnabled()) {
    return {
      payload: normalized,
      backend: 'disabled',
    };
  }
  const saveResult = await saveReportDraft(storageKey, normalized);
  return {
    payload: normalized,
    backend: saveResult?.backend || null,
  };
};

export const markSyncSuccess = async ({ storageKey, payload, serverDraft }) => {
  const normalized = normalizeDraftPayload(payload);
  if (!normalized) {
    return null;
  }
  const nextPayload = normalizeDraftPayload(normalized, {
    sync: {
      ...normalized.sync,
      status: 'synced',
      serverRevision: toNumberOrDefault(serverDraft?.serverRevision, normalized.sync?.serverRevision ?? 0),
      lastSyncedAt: sanitizeTimestamp(serverDraft?.updatedAt, new Date().toISOString()),
      lastErrorCode: null,
      lastErrorMessage: null,
      retryAt: null,
    },
  });
  if (!nextPayload) {
    return null;
  }
  if (isReportDraftLocalCacheEnabled()) {
    await saveReportDraft(storageKey, nextPayload);
  }
  return nextPayload;
};

export const markSyncError = async ({ storageKey, payload, status, errorCode, message, retryAt }) => {
  const normalized = normalizeDraftPayload(payload);
  if (!normalized) {
    return null;
  }
  const nextPayload = normalizeDraftPayload(normalized, {
    sync: {
      ...normalized.sync,
      status: status || 'sync_error',
      lastErrorCode: errorCode || null,
      lastErrorMessage: message || null,
      retryAt: sanitizeTimestamp(retryAt, null),
    },
  });
  if (!nextPayload) {
    return null;
  }
  if (isReportDraftLocalCacheEnabled()) {
    await saveReportDraft(storageKey, nextPayload);
  }
  return nextPayload;
};

export const replaceLocalWithServerDraft = async ({ storageKey, localDraft, serverDraft }) => {
  const nextPayload = mapServerDraftToLocalPayload(serverDraft, { localDraft });
  if (!nextPayload) {
    return null;
  }
  if (isReportDraftLocalCacheEnabled()) {
    await saveReportDraft(storageKey, nextPayload);
  }
  return nextPayload;
};

export const syncDraftToServer = async ({
  reportId,
  locale,
  payload,
  baseServerRevision = null,
  force = false,
  signal,
} = {}) => {
  const normalized = normalizeDraftPayload(payload, { reportId, locale });
  if (!normalized) {
    throw new Error('Cannot sync draft: invalid payload');
  }

  const serverDraft = await putReportDraftToServer({
    reportId: normalized.reportId,
    locale: normalized.locale,
    payload: normalized,
    baseServerRevision,
    force,
    signal,
  });

  return {
    requestPayload: normalized,
    serverDraft,
  };
};

export const loadBestDraft = async ({ reportId, locale, storageKey }) => {
  const localCacheEnabled = isReportDraftLocalCacheEnabled();
  const localPromise = localCacheEnabled
    ? loadReportDraft(storageKey).catch(() => null)
    : Promise.resolve(null);
  const remotePromise = getReportDraftFromServer({ reportId, locale }).catch((error) => {
    if (error instanceof ReportDraftApiError && (error.code === 'network_error' || error.status >= 500)) {
      return null;
    }
    return null;
  });

  const [rawLocal, serverDraft] = await Promise.all([localPromise, remotePromise]);
  const localDraft = normalizeDraftPayload(rawLocal, { reportId, locale });
  const serverBackedLocalDraft = mapServerDraftToLocalPayload(serverDraft, {
    reportId,
    locale,
    localDraft,
  });
  const chosen = chooseBestDraft(localDraft, serverBackedLocalDraft);

  if (!chosen) {
    return null;
  }

  const shouldPersistChosen = (() => {
    if (!localDraft) {
      return true;
    }
    const localSerialized = getSerializedEditorState(localDraft);
    const chosenSerialized = getSerializedEditorState(chosen);
    if (localSerialized !== chosenSerialized) {
      return true;
    }
    const localServerRevision = toNumberOrDefault(localDraft.sync?.serverRevision, 0);
    const chosenServerRevision = toNumberOrDefault(chosen.sync?.serverRevision, 0);
    return localServerRevision !== chosenServerRevision
      || (localDraft.sync?.status || '') !== (chosen.sync?.status || '');
  })();

  if (localCacheEnabled && shouldPersistChosen) {
    try {
      await saveReportDraft(storageKey, chosen);
    } catch (error) {
      // Best-effort cache update only.
    }
  }

  return chosen;
};

export const flushPendingSyncQueue = async () => {
  // Sync queue is managed inside the editor autosave plugin instance for now.
  return null;
};

export {
  isReportDraftLocalCacheEnabled,
};
